import actionTypes from 'redux/actions/actionTypes'
import makeUniqueId from 'helpers/makeUniqueId'
import errorCodes from 'helpers/enums/errorCodes'

const defaultTimer = {
	value: null,
}

const defaultState = {
	entityUID: '',
	presentationId: '',
	questions: [],
	settings: {
		allowMultipleOptions: '1',
	},
	isModified: false,
	timer: {
		...defaultTimer,
	},
	cancelUrl: '',
}

const defaultIcon = {
	id: 0,
	uploadId: 0,
	type: '',
	versions: {},
	link: '',
	status: '',
	message: '',
}

const defaultQuestion = {
	id: '',
	serverId: '',
	icon: {},
	text: '',
	textTouched: true,
	answers: [],
	collapsed: false,
	touched: true,
	status: '',
}

const defaultAnswer = {
	id: '',
	text: '',
	textTouched: true,
}

function addAnswer(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					answers: [ ...question.answers, {...defaultAnswer, id: makeUniqueId()} ],
					status: 'saving',
				}
			} else {
				return question
			}
		}),
	}
}

function addReferenceMediaOpenMenu(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					icon: {
						...question.icon,
						message: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function answerChange(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					answers: question.answers.map(answer => {
						if (answer.id === action.answerId) {
							return {
								...answer,
								text: action.value,
							}
						} else {
							return answer
						}
					}),
				}
			} else {
				return question
			}
		}),
	}
}

function answerFocus(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					answers: question.answers.map(answer => {
						if (answer.id === action.answerId) {
							return {
								...answer,
								textTouched: true,
							}
						} else {
							return answer
						}
					}),
				}
			} else {
				return question
			}
		}),
	}
}

function deleteReferenceMedia(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					icon: {
						...defaultIcon,
					},
				}
			} else {
				return question
			}
		}),
	}
}

function deleteAnswer(state, action) {
	const newState = {...state}
	newState.isModified = true
	const questionIndex = state.questions.findIndex(question => question.id === action.questionId)

	newState.questions = [...state.questions]
	newState.questions[questionIndex] = {
		...state.questions[questionIndex],
		answers: state.questions[questionIndex].answers.filter(answer => answer.id !== action.answerId),
		status: 'saving',
	}

	return newState
}

function initialize(state, action) {
	return {
		...state,
		entityUID: action.pollUID,
	}
}

function moveAnswer(state, action) {

	const newState = {...state}
	newState.questions = [...state.questions]

	// search dragged and replaced question position
	const draggedQuestionIndex = state.questions.findIndex(question => question.id === action.draggedAnswer.questionId)

	// search dragged and replaced answers positions
	const draggedAnswerIndex = state.questions[draggedQuestionIndex].answers.findIndex(
		answer => answer.id === action.draggedAnswer.answerId
	)
	const replacedAnswerIndex = state.questions[draggedQuestionIndex].answers.findIndex(
		answer => answer.id === action.replacedAnswer.answerId
	)

	newState.questions[draggedQuestionIndex] = {...state.questions[draggedQuestionIndex]}
	newState.questions[draggedQuestionIndex].answers = [...state.questions[draggedQuestionIndex].answers]

	// move replaced to dragged position
	newState.questions[draggedQuestionIndex].answers[draggedAnswerIndex] = {
		...state.questions[draggedQuestionIndex].answers[replacedAnswerIndex],
	}

	// move dragged to replaced position
	newState.questions[draggedQuestionIndex].answers[replacedAnswerIndex] = {
		...state.questions[draggedQuestionIndex].answers[draggedAnswerIndex],
	}

	return newState
}

function questionChange(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					text: action.value,
					status: 'changed',
					hasExceedChars: false,
				}
			} else {
				return question
			}
		}),
	}
}

function questionFocus(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					textTouched: true,
				}
			} else {
				return question
			}
		}),
	}
}

function questionSaveSuccess(state, action) {
	return {
		...state,
		isModified: true,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					serverId: action.serverId,
					status: '',
				}
			} else {
				return question
			}
		}),
	}
}

function questionUpdate(state, action) {
	return {
		...state,
		isModified: true,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					status: 'saving',
				}
			} else {
				return question
			}
		}),
	}
}

function questionUpdateSuccess(state, action) {
	return {
		...state,
		isModified: true,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					status: '',
				}
			} else {
				return question
			}
		}),
	}
}

function uploaderValidateSuccess(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					icon: {
						...question.icon,
						uploadId: action.uploadId,
					},
				}
			} else {
				return question
			}
		}),
	}
}

function saveSettings(state, action) {
	return {
		...state,
		settings: {
			...state.settings,
			...action.settings,
		},
	}
}

function setPollAsModified(state) {
	if (state.isModified) {
		return state
	}
	return {
		...state,
		isModified: true,
	}
}

function validationError(state) {
	return {
		...state,
		questions: state.questions.map(question => ({
			...question,
			textTouched: false,
			touched: false,
			answers: question.answers.map(answer => ({
				...answer,
				textTouched: false,
			})),
		})),
	}
}

function questionUpdateError(state, action) {
	return {
		...state,
		questions: state.questions.map(question => ({
			...question,
			textTouched: false,
			touched: false,
			status: '',
			hasExceedChars: action.error.error_code === errorCodes.EXCEED_MAX_CHARS,
		})),
	}
}

function iconCreateInitialize(icon) {
	if (icon.id) {
		return {
			...defaultIcon,
			id: icon.id,
			uploadId: 0,
			type: icon.type,
			versions: icon.versions,
			link: icon.link,
			status: icon.status,
			message: '',
		}
	} else {
		return {
			...defaultIcon,
		}
	}
}

function uploaderUploadError(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					icon: {
						...question.icon,
						message: action.message,
						status: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function uploaderUploadSuccess(state, action) {
	return {
		...state,
		isModified: true,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					icon: {
						...question.icon,
						id: action.icon.id,
						type: action.icon.type,
						versions: action.icon.versions,
						link: action.icon.link,
						status: action.icon.status,
						message: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function processingIconSuccess(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.icon.id === action.icon.id) {
				return {
					...question,
					icon: {
						...question.icon,
						...action.icon,
						message: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function processingIconError(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.icon.id === action.action.icon.id) {
				return {
					...question,
					icon: {
						...question.icon,
						message: action.message,
						status: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function answerCreateInitialize(answers) {
	return answers.map(answer => ({
		...defaultAnswer,
		id: makeUniqueId(),
		text: answer.text,
	}))
}

function questionCreateInitialize(questions) {
	return questions.map(question => ({
		...defaultQuestion,
		id: makeUniqueId(),
		serverId: question.id,
		text: question.text,
		answers: answerCreateInitialize(question.answers),
		icon: iconCreateInitialize(question.icon),
	}))
}

function initializeSettings(questions) {
	if (questions[0]) {
		if ((questions[0].type === '') || (questions[0].type === 'multipleChoice')) {
			return {
				allowMultipleOptions: '1',
			}
		} else if (questions[0].type === 'singleChoice') {
			return {
				allowMultipleOptions: '0',
			}
		}
	}
}

function initializeSuccess(state, action) {
	return {
		...state,
		presentationId: action.data.presentationId,
		questions: questionCreateInitialize(action.data.questions),
		settings: initializeSettings(action.data.questions),
		timer: {
			value: action.data.timer,
		},
		cancelUrl: action.data.cancelUrl,
	}
}

function showReferenceMediaLoading(state, action) {
	return {
		...state,
		questions: state.questions.map(question => {
			if (question.id === action.questionId) {
				return {
					...question,
					icon: {
						...question.icon,
						status: 'uploading',
						message: '',
					},
				}
			} else {
				return question
			}
		}),
	}
}

function saveTimer(state, action) {
	//Adding previous timer value to action in case it needs to be reverted if service fails when saving
	action.previous_timer = state.timer
	return {
		...state,
		isModified: true,
		timer: {
			...state.timer,
			value: action.value,
		},
	}
}

function revertTimer(state, action) {
	return {
		...state,
		isModified: true,
		timer: action.previous_timer,
	}
}

function deleteTimer(state, action) {
	//Adding previous timer value to action in case it needs to be reverted if service fails when saving
	action.previous_timer = state.timer
	return {
		...state,
		timer: defaultTimer,
		isModified: true,
	}
}

const pollReducer = (state = defaultState, action) => {
	switch (action.type) {
		case actionTypes.POLL_ADD_ANSWER_VALIDATION_SUCCESS: {
			return addAnswer(state, action)
		}
		case actionTypes.POLL_ADD_REFERENCE_MEDIA_OPEN_MENU: {
			return addReferenceMediaOpenMenu(state, action)
		}
		case actionTypes.POLL_ANSWER_CHANGE: {
			return answerChange(state, action)
		}
		case actionTypes.POLL_ANSWER_FOCUS: {
			return answerFocus(state, action)
		}
		case actionTypes.POLL_DELETE_ANSWER_VALIDATION_SUCCESS: {
			return deleteAnswer(state, action)
		}
		case actionTypes.POLL_DELETE_REFERENCE_MEDIA: {
			return deleteReferenceMedia(state, action)
		}
		case actionTypes.POLL_DRAG_ANSWER: {
			return moveAnswer(state, action)
		}
		case actionTypes.POLL_DROP_ANSWER:
		{
			return setPollAsModified(state, action)
		}
		case actionTypes.POLL_INITIALIZE_SUCCESS: {
			return initializeSuccess(state, action)
		}
		case actionTypes.POLL_INITIALIZE_CREATION_TOOL:
		case actionTypes.POLL_INITIALIZE_STANDALONE: {
			return initialize(state, action)
		}
		case actionTypes.POLL_QUESTION_CHANGE: {
			return questionChange(state, action)
		}
		case actionTypes.POLL_QUESTION_FOCUS: {
			return questionFocus(state, action)
		}
		case actionTypes.POLL_QUESTION_UPDATE_SUCCESS: {
			return questionUpdateSuccess(state, action)
		}
		case actionTypes.POLL_QUESTION_UPDATE_ERROR: {
			return questionUpdateError(state, action)
		}
		case actionTypes.POLL_ANSWER_SAVE:
		case actionTypes.POLL_QUESTION_SAVE: {
			return questionUpdate(state, action)
		}
		case actionTypes.POLL_QUESTION_SAVE_SUCCESS: {
			return questionSaveSuccess(state, action)
		}
		case actionTypes.POLL_SAVE_SETTINGS: {
			return saveSettings(state, action)
		}
		case actionTypes.POLL_UPLOAD_REFERENCE_MEDIA: {
			return showReferenceMediaLoading(state, action)
		}
		case actionTypes.POLL_UPLOAD_REFERENCE_MEDIA_ERROR:
		case actionTypes.POLL_UPLOADER_VALIDATE_ERROR:
		case actionTypes.POLL_UPLOADER_UPLOAD_ERROR:
		case actionTypes.POLL_UPLOADER_VALIDATE_CONTENT_ERROR: {
			return uploaderUploadError(state, action)
		}
		case actionTypes.POLL_UPLOADER_VALIDATE_SUCCESS: {
			return uploaderValidateSuccess(state, action)
		}
		case actionTypes.POLL_UPLOADER_UPLOAD_SUCCESS: {
			return uploaderUploadSuccess(state, action)
		}
		case actionTypes.POLL_VALIDATION_ERROR: {
			return validationError(state, action)
		}
		case actionTypes.POLL_SAVE_TIMER:
			return saveTimer(state, action)
		case actionTypes.POLL_TIMER_SAVE_ERROR:
			return revertTimer(state, action)
		case actionTypes.POLL_DELETE_TIMER:
			return deleteTimer(state, action)
		case actionTypes.PROCESSING_ICON_SUCCESS: {
			return processingIconSuccess(state, action)
		}
		case actionTypes.PROCESSING_ICON_ERROR: {
			return processingIconError(state, action)
		}
		default: return state
	}
}
export default pollReducer
